import Fuse from 'fuse.js';
import { client } from '@utility/graphql';
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer';

// In-memory cache — shared across all instances on the page
let _faqCache = null;
let _fuseInstance = null;
let _loadPromise = null;

const FAQ_QUERY = `
  query GetFaqs {
    metaobjects(type: "faq", first: 250) {
      nodes {
        id
        fields { key value }
      }
    }
  }
`;

function parseFaqNode(node) {
  const f = Object.fromEntries(node.fields.map(({ key, value }) => [key, value]));

  // Walk the Shopify rich text JSON schema to extract plain text for searching
  const walkSchema = (n) => {
    if (!n) return '';
    if (n.value) return n.value;
    if (n.children) return n.children.map(walkSchema).join(' ');
    return '';
  };

  let answerText = '';
  try { answerText = walkSchema(JSON.parse(f.answer || 'null')); } catch (e) {
    answerText = f.answer || '';
  }

  let tags = [];
  try { tags = JSON.parse(f.tags || '[]'); } catch (e) {
    console.error(e);
  }

  return {
    id: node.id,
    question: f.question || '',
    answerText,
    answerRaw: f.answer || '',
    tags,
    boost: parseInt(f.boost) || 0,
  };
}

function loadFaqs() {
  if (_faqCache) return Promise.resolve(_faqCache);
  if (_loadPromise) return _loadPromise;

  _loadPromise = client.request(FAQ_QUERY)
    .then(({ metaobjects }) => {
      _faqCache = metaobjects.nodes.map(parseFaqNode);
      _fuseInstance = new Fuse(_faqCache, {
        keys: [
          { name: 'question',   weight: 3 },
          { name: 'tags',       weight: 2 },
          { name: 'answerText', weight: 1 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
        includeScore: true,
      });
      return _faqCache;
    })
    .catch((e) => {
      _loadPromise = null; // allow retry on next input
      return Promise.reject(e);
    });

  return _loadPromise;
}

function searchFaqs(query) {
  if (!_fuseInstance || !query.trim()) return [];

  return _fuseInstance.search(query)
    .map(({ item, score }) => ({ item, adj: (score ?? 0) - item.boost * 0.05 }))
    .sort((a, b) => a.adj - b.adj)
    .slice(0, 5)
    .map(({ item }) => item);
}

export class FaqSearch extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    const selector = this.getAttribute('input-selector');
    if (selector) {
      this._input = document.querySelector(selector);
    } else {
      const form = this.closest('form');
      if (!form) {
        return;
      }
      this._input = form.querySelector('input[name="q"]');
    }

    if (!this._input) {
      return;
    }

    const noResultsEl = this.querySelector('[data-no-results]');
    this._noResultsHtml = noResultsEl?.outerHTML ?? '';
    noResultsEl?.remove();

    // Prefetch on connect so results are instant once the user types
    loadFaqs()

    this._input.addEventListener('input', this._onInput.bind(this), { signal });

    if (this._input.value.trim()) {
      this._search(this._input.value);
    }
  }

  disconnectedCallback() {
    clearTimeout(this._debounce);
    this._abortController.abort();
  }

  _onInput() {
    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this._search(this._input.value), 280);
  }

  async _search(query) {
    
    if (!query.trim()) {
      this.hidden = true;
      return;
    }

    try {
      await loadFaqs();
    } catch (e) {
      return;
    }

    const results = searchFaqs(query);
    

    if (results.length === 0) {
      if (this._noResultsHtml) {
        this.setAttribute('no-results', '');
        this.hidden = false;
        this.innerHTML = this._noResultsHtml;
      } else {
        this.hidden = true;
      }
      return;
    }

    this.removeAttribute('no-results');
    this.hidden = false;
    this._render(results);
    
  }

  _render(results) {
    this.innerHTML = `
      ${this.hasAttribute('hide-title') ? '' : '<h5 class="h5 mb-2">FAQs</h5>'}
      <ul class="v-stack gap-3" role="list">
        ${results.map(faq => this._renderItem(faq)).join('')}
      </ul>
    `;

  }

  _renderItem(faq) {
    let answerHtml = '';
    try {
      answerHtml = convertSchemaToHtml(faq.answerRaw);
    } catch (_) {
      answerHtml = `<p>${faq.answerText}</p>`;
    }

    const id = `faq-${faq.id.split('/').pop()}`;

    return `
      <li role="listitem">
        <details id="${id}" class="accordion group" aria-expanded="false" is="accordion-disclosure">
          <summary>
            <div class="accordion__toggle bold">
              <span class="text-base">${faq.question}</span>
              <span class="circle-chevron group-hover:colors group-expanded:colors group-expanded:rotate">
                <svg role="presentation" focusable="false" width="8" height="6" viewBox="0 0 8 6">
                  <path d="m1 1.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
                </svg>
              </span>
            </div>
          </summary>
          <div class="accordion__content prose text-base">${answerHtml}</div>
        </details>
      </li>
    `;
  }
}
